import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { resolveMediaUrl } from '../../lib/mediaUrl';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Full-bleed crossfading background for client advertising slides. It only
 * draws the slides and the wash that keeps the foreground legible; the parent
 * decides what sits on top. Pauses while the tab is hidden, and shows a single
 * static slide for visitors who prefer reduced motion.
 */
const BackgroundSlideshow = ({ slides, seconds = 6, dark = false }) => {
  // `prev` stays mounted just long enough to fade out. Only these two slides
  // are in the DOM, so any number of slides costs the same as two.
  const [pos, setPos] = useState({ index: 0, prev: null });
  const count = slides.length;
  const goTo = (next) => setPos((p) => (p.index === next ? p : { index: next, prev: p.index }));

  useEffect(() => {
    if (count < 2 || prefersReducedMotion()) return undefined;
    let timer;
    const stop = () => clearInterval(timer);
    const start = () => {
      stop();
      timer = setInterval(() => setPos((p) => ({ index: (p.index + 1) % count, prev: p.index })), seconds * 1000);
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [count, seconds]);

  // Warm the browser cache for the next slide so the crossfade never shows a blank.
  useEffect(() => {
    if (count < 2) return;
    new Image().src = resolveMediaUrl(slides[(pos.index + 1) % count].image_url);
  }, [slides, count, pos.index]);

  if (!count) return null;
  const active = pos.index % count;
  const prev = pos.prev !== null && pos.prev % count !== active ? pos.prev % count : null;
  const current = slides[active];

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="transfer-slideshow">
      {[prev, active].filter((i) => i !== null).map((i) => (
        <img
          key={`${i}-${slides[i].id || slides[i].image_url}`}
          src={resolveMediaUrl(slides[i].image_url)}
          alt={slides[i].alt || ''}
          aria-hidden={slides[i].alt ? undefined : true}
          className={`absolute inset-0 w-full h-full object-cover ${
            i === active ? 'slide-fade-in' : 'slide-fade-out'
          }`}
        />
      ))}

      {/* Wash: keeps the card and headline readable over any client artwork. */}
      <div className={`absolute inset-0 ${dark ? 'bg-black/55' : 'bg-white/45'}`} />
      <div className={`absolute inset-0 bg-gradient-to-r ${dark ? 'from-black/40' : 'from-white/60'} via-transparent to-transparent`} />

      {(current.advertiser || current.sponsor_url) && (
        <div className="absolute left-4 bottom-16 sm:bottom-4 z-10 max-w-[calc(100%-2rem)] sm:max-w-[45%] flex flex-wrap items-center gap-2 text-xs" data-testid="slide-sponsor">
          {current.advertiser && (
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-black/60 text-white">Ad · {current.advertiser}</span>
          )}
          {current.sponsor_url && (
            // "sponsored" is the right rel for a paid link. No "noreferrer": we WANT the sponsor
            // to see this site as the referrer - "origin" sends only the site's address, never a page path.
            <a
              href={current.sponsor_url}
              target="_blank"
              rel="noopener sponsored"
              referrerPolicy="origin"
              className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-full bg-white text-slate-900 font-semibold shadow-lg hover:bg-slate-100"
              data-testid="visit-sponsor"
            >
              Visit sponsor <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
      )}

      {count > 1 && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 flex items-center rounded-full bg-black/60 text-white text-xs">
          <button type="button" aria-label="Previous slide" onClick={() => goTo((active - 1 + count) % count)}
            className="w-11 h-11 inline-flex items-center justify-center">‹</button>
          <span aria-live="off">{active + 1} / {count}</span>
          <button type="button" aria-label="Next slide" onClick={() => goTo((active + 1) % count)}
            className="w-11 h-11 inline-flex items-center justify-center">›</button>
        </div>
      )}
    </div>
  );
};

export default BackgroundSlideshow;
